import { useEffect, useRef, useState } from 'react';

interface TradingViewChartProps {
  symbol?: string;
  height?: number;
}

export const TradingViewChart = ({ 
  symbol = "BINANCE:BTCUSDT",
  height = 500 
}: TradingViewChartProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isReady, setIsReady] = useState(false);
  const widgetId = useRef(`tradingview-chart-${Date.now()}`);
  const scriptRef = useRef<HTMLScriptElement | null>(null);

  // Wait for DOM to be fully ready
  useEffect(() => {
    const timer = requestAnimationFrame(() => {
      setIsReady(true);
    });
    return () => cancelAnimationFrame(timer);
  }, []);

  useEffect(() => {
    if (!isReady || !containerRef.current) return;

    const container = containerRef.current;
    
    // Clear any existing content safely
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }

    // Create container for the widget
    const widgetContainer = document.createElement('div');
    widgetContainer.id = widgetId.current;
    widgetContainer.style.height = `${height}px`;
    container.appendChild(widgetContainer);

    // Check if script already exists
    const existingScript = document.querySelector('script[src="https://s3.tradingview.com/tv.js"]');
    
    const initWidget = () => {
      if (typeof (window as any).TradingView !== 'undefined' && document.getElementById(widgetId.current)) {
        try {
          new (window as any).TradingView.widget({
            width: '100%',
            height: height,
            symbol: symbol,
            interval: 'D',
            timezone: 'Australia/Sydney',
            theme: 'dark',
            style: '1',
            locale: 'en',
            toolbar_bg: '#f1f3f6',
            enable_publishing: false,
            allow_symbol_change: true,
            container_id: widgetId.current,
          });
        } catch (error) {
          console.warn('TradingView widget initialization error:', error);
        }
      }
    };

    if (existingScript) {
      // Script already loaded, just initialize widget
      setTimeout(initWidget, 100);
    } else {
      // Load TradingView script
      const script = document.createElement('script');
      script.src = 'https://s3.tradingview.com/tv.js';
      script.async = true;
      script.onload = () => {
        setTimeout(initWidget, 100);
      };
      script.onerror = () => {
        console.warn('Failed to load TradingView script');
      };
      document.head.appendChild(script);
      scriptRef.current = script;
    }

    return () => {
      // Clean up container
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
    };
  }, [symbol, height, isReady]);

  return (
    <div className="w-full rounded-lg overflow-hidden border border-border/50">
      <div 
        ref={containerRef}
        style={{ minHeight: `${height}px` }}
      />
    </div>
  );
};
